import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import { IconPlus, IconTrophy } from "@tabler/icons-react";
import {
  Badge,
  Button,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import "./OrganizationContent.css";

function AdminTabIncentivesPage() {
  const navigate = useNavigate();
  const { user, getVerifiedJWT } = useAuth();

  const [incentives, setIncentives] = useState([]);
  const [loadingIncentives, setLoadingIncentives] = useState(true);
  const [incentiveStatusFilter, setIncentiveStatusFilter] = useState("all");

  const canManageIncentives =
    user && (user.role === "MANAGER" || user.role === "COMPANY_ADMIN");

  const fetchIncentives = async () => {
    try {
      setLoadingIncentives(true);
      const jwt = await getVerifiedJWT();
      const response = await fetch(getApiEndpoint("/v1/incentives"), {
        headers: { Authorization: `Bearer ${jwt}` },
      });

      if (response.ok) {
        const result = await response.json();
        setIncentives(result.data || []);
      } else {
        setIncentives([]);
      }
    } catch (error) {
      setIncentives([]);
    } finally {
      setLoadingIncentives(false);
    }
  };

  useEffect(() => {
    if (!canManageIncentives) return;
    fetchIncentives();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const formatDate = dateString => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const filteredIncentives = incentives.filter(incentive => {
    if (incentiveStatusFilter === "all") return true;
    return incentive.status === incentiveStatusFilter;
  });

  const activeCount = incentives.filter(i => i.status === "active").length;
  const draftCount = incentives.filter(i => i.status === "draft").length;
  const inactiveCount = incentives.filter(
    i => i.status === "inactive" || i.status === "disabled"
  ).length;

  if (loadingIncentives) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
        <Text c="dimmed" size="sm">
          Loading incentives...
        </Text>
      </Group>
    );
  }

  return (
    <div className="org-section-content">
      <div className="carousel-section">
        <Group justify="space-between" mb="md">
          <div>
            <Title order={3}>Incentives</Title>
            <Text c="dimmed" size="sm">
              {incentives.length}{" "}
              {incentives.length === 1 ? "incentive" : "incentives"}
            </Text>
          </div>
          {canManageIncentives && (
            <Button
              leftSection={<IconPlus size={14} />}
              onClick={() => navigate("/incentives/new")}
              variant="default"
              size="md"
              radius="md"
              style={{
                backgroundColor: "white",
                border: "1px solid #dee2e6",
                color: "#2d3748",
                fontWeight: 500,
              }}
            >
              New Incentive
            </Button>
          )}
        </Group>

        <Group gap="xs" mb="xl">
          <Button
            size="sm"
            variant="default"
            radius="md"
            onClick={() => setIncentiveStatusFilter("all")}
            style={{
              backgroundColor:
                incentiveStatusFilter === "all" ? "#f1f3f5" : "white",
              border: "1px solid #dee2e6",
              color: "#2d3748",
              fontWeight: incentiveStatusFilter === "all" ? 600 : 400,
            }}
          >
            All
          </Button>
          <Button
            size="sm"
            variant="default"
            radius="md"
            onClick={() => setIncentiveStatusFilter("active")}
            style={{
              backgroundColor:
                incentiveStatusFilter === "active" ? "#f1f3f5" : "white",
              border: "1px solid #dee2e6",
              color: "#2d3748",
              fontWeight: incentiveStatusFilter === "active" ? 600 : 400,
            }}
          >
            Active ({activeCount})
          </Button>
          <Button
            size="sm"
            variant="default"
            radius="md"
            onClick={() => setIncentiveStatusFilter("draft")}
            style={{
              backgroundColor:
                incentiveStatusFilter === "draft" ? "#f1f3f5" : "white",
              border: "1px solid #dee2e6",
              color: "#2d3748",
              fontWeight: incentiveStatusFilter === "draft" ? 600 : 400,
            }}
          >
            Draft ({draftCount})
          </Button>
          <Button
            size="sm"
            variant="default"
            radius="md"
            onClick={() => setIncentiveStatusFilter("inactive")}
            style={{
              backgroundColor:
                incentiveStatusFilter === "inactive" ? "#f1f3f5" : "white",
              border: "1px solid #dee2e6",
              color: "#2d3748",
              fontWeight: incentiveStatusFilter === "inactive" ? 600 : 400,
            }}
          >
            Inactive ({inactiveCount})
          </Button>
        </Group>

        {filteredIncentives.length === 0 ? (
          <Stack align="center" gap="md" py="xl">
            <IconTrophy size={48} color="var(--mantine-color-orange-5)" />
            <Text ta="center" c="dimmed" size="lg" fw={500}>
              No incentives yet
            </Text>
            <Text ta="center" c="dimmed" size="sm">
              Create milestones or competitions with prizes to motivate your
              team.
            </Text>
            {canManageIncentives && (
              <Button
                leftSection={<IconPlus size={14} />}
                onClick={() => navigate("/incentives/new")}
                variant="light"
                color="orange"
              >
                Create Your First Incentive
              </Button>
            )}
          </Stack>
        ) : (
          <div className="incentive-cards-grid">
            {filteredIncentives.map(incentive => (
              <div
                key={incentive.id}
                className="incentive-card"
                onClick={() => {
                  if (canManageIncentives) {
                    navigate(`/incentives/edit/${incentive.id}`);
                  }
                }}
                style={{ cursor: canManageIncentives ? "pointer" : "default" }}
              >
                <div className="incentive-card-thumbnail">
                  {incentive.banner_url ? (
                    <img
                      src={incentive.banner_url}
                      alt={incentive.title}
                      className="incentive-card-image"
                    />
                  ) : (
                    <div className="incentive-card-placeholder">
                      <IconTrophy size={40} color="#f97316" />
                    </div>
                  )}
                  <Badge
                    className="incentive-card-status-badge"
                    color={
                      incentive.status === "active"
                        ? "green"
                        : incentive.status === "draft"
                          ? "gray"
                          : "blue"
                    }
                    variant="filled"
                    size="sm"
                  >
                    {incentive.status}
                  </Badge>
                </div>
                <div className="incentive-card-content">
                  <h3 className="incentive-card-title">{incentive.title}</h3>
                  <span className="incentive-card-dates">
                    {formatDate(incentive.start_date)} —{" "}
                    {formatDate(incentive.end_date)}
                  </span>
                  <div className="incentive-card-meta">
                    <Badge color="dark" variant="outline" size="md">
                      {incentive.type === "milestone"
                        ? "Milestone"
                        : "Competition"}
                    </Badge>
                    {incentive.type === "competition" &&
                      incentive.competition_level && (
                        <Badge color="dark" variant="outline" size="md">
                          {incentive.competition_level === "individual"
                            ? "Individual"
                            : "Team"}
                        </Badge>
                      )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminTabIncentivesPage;
